import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SelectField, Option } from '@components/ui/formElements/form/selectFields/SelectField'
import { useCurrency } from '@helpers/context/CurrencyContext'
import { getSelectedCurrency } from '@helpers/currency'
import { checkStoreIs, getNativeCurrencyCode, replaceLocale } from '@helpers/env'
import { setCookie } from '@helpers/session'
import { analytics } from '@helpers/analytics'
import useIsReactClient from '@helpers/hooks/useIsReactClient'
import { COOKIE_EXPIRE_1_MONTH, COOKIE_EXPIRE_1_YEAR, SELECTED_CURRENCY_COOKIE } from '@consts/index'
import { CURRENCY_OPTIONS, CurrencyOption } from '@consts/countryCurrencySelector'
import styles from './styles.module.scss'

const STORES = Array.from(new Set(CURRENCY_OPTIONS.map(option => option.store)))

type FormValues = { selectedRegion: string }

function getCurrentStore(): CurrencyOption['store'] {
  return STORES.find(store => checkStoreIs(store)) || STORES[0]
}

function getOptionLabel(option: CurrencyOption): string {
  return `${option.label} (${option.symbol} ${option.currency})`
}

// Both currency-switch paths below reload/navigate the whole page. Browsers
// restore the previous scroll position on reload by default, which would
// leave the page sitting at the footer where the switcher lives. Disabling
// scroll restoration makes the next load start at the top instead.
function scrollToTopBeforeReload(): void {
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
  }
  window.scrollTo(0, 0)
}

// Prefer the currency cookie directly over CurrencyContext's own
// `selectedCurrency`, which always starts at the native currency and only
// corrects itself once its Shopify fetch resolves — reading it here instead
// caused a GBP-then-AUD flicker on every reload for a currency that was
// already known. The cookie is available synchronously; CurrencyContext's
// value is only needed as a fallback for a first-time visitor whose currency
// hasn't been geo-derived (and cookie-set) yet.
function resolveCurrentCurrency(
  storeOptions: CurrencyOption[],
  contextCurrency: string
): string {
  const cookieCurrency = getSelectedCurrency()
  if (cookieCurrency && storeOptions.some(option => option.currency === cookieCurrency)) {
    return cookieCurrency
  }

  if (storeOptions.some(option => option.currency === contextCurrency)) {
    return contextCurrency
  }

  return getNativeCurrencyCode()
}

const options: Option[] = CURRENCY_OPTIONS.map(option => ({
  value: option.value,
  label: getOptionLabel(option),
  available: true,
  icon: (
    <option.FlagIcon
      className={styles.flagIcon}
      preserveAspectRatio="xMidYMid slice"
    />
  )
}))

export default function CountryCurrencySwitcher() {
  const isReactClient = useIsReactClient()
  const { selectedCurrency, setSelectedCurrency } = useCurrency()

  if (!isReactClient) {
    return null
  }

  const currentStore = getCurrentStore()
  const storeOptions = CURRENCY_OPTIONS.filter(option => option.store === currentStore)
  const currentCurrency = resolveCurrentCurrency(storeOptions, selectedCurrency)
  const selectedOption = storeOptions.find(option => option.currency === currentCurrency) || storeOptions[0]

  return (
    <div className={styles.CountryCurrencySwitcher}>
      {/*
        Keyed by the resolved selection: if it changes externally (e.g. the
        cookie-derived currency resolving after a reload), this remounts the
        form fresh with the correct defaultValue instead of imperatively
        syncing an existing form's value — which raced with reacting to the
        user's own selection (see git history for the details of that bug).
      */}
      <RegionForm
        key={selectedOption.value}
        selectedOption={selectedOption}
        currentStore={currentStore}
        setSelectedCurrency={setSelectedCurrency}
      />
    </div>
  )
}

type RegionFormProps = {
  selectedOption: CurrencyOption
  currentStore: CurrencyOption['store']
  setSelectedCurrency: (code: string) => void
}

function RegionForm({
  selectedOption,
  currentStore,
  setSelectedCurrency
}: RegionFormProps) {
  const methods = useForm<FormValues>({
    defaultValues: { selectedRegion: selectedOption.value }
  })
  const watchedValue = methods.watch('selectedRegion')

  useEffect(() => {
    if (watchedValue === selectedOption.value) return

    const option = CURRENCY_OPTIONS.find(o => o.value === watchedValue)
    if (!option) return

    if (option.store === currentStore) {
      scrollToTopBeforeReload()
      setSelectedCurrency(option.currency)
      return
    }

    setCookie(SELECTED_CURRENCY_COOKIE, option.currency, COOKIE_EXPIRE_1_YEAR)
    setCookie('th_chosen', option.store, COOKIE_EXPIRE_1_MONTH, true)
    analytics('Click', {
      category: 'Country Switcher',
      label: `Country Switcher ${option.label} ${option.currency}`
    })
    scrollToTopBeforeReload()
    window.location.href = replaceLocale(
      option.locale === 'gb' ? '' : `/${option.locale}`
    )
    // selectedOption/currentStore are stable for the lifetime of this
    // component instance — it remounts (fresh key) whenever they'd change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValue, setSelectedCurrency])

  return (
    <FormProvider {...methods}>
      <SelectField<FormValues>
        id="selectedRegion"
        name="Region"
        forceDirection="up"
        options={options}
        defaultValue={selectedOption.value}
        settings={{ label: { visible: false } }}
      />
    </FormProvider>
  )
}
